import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'

import { FormattedMessage, useIntl } from 'react-intl'

import { formsMessages } from '@/lib/core/messages'
import { Button } from '@/lib/core/components/Button'
import { TextField } from '@/lib/core/components/TextField'
import { SelectField } from '@/lib/core/components/Select'

import { useAddVehicle } from '@/lib/features/Assets/api'
import { useAssetFormState } from '@/lib/features/Assets/components/AddAssetModal/store/useAssetFormState'
import { useTrackingSourceOptions } from '@/lib/features/Assets/components/AddAssetModal/hooks/useTrackingSourceOptions'
import {
  AddAssetVehicleError,
  AddAssetVehiclePayload,
  AddAssetVehicleResponse,
} from '@/lib/features/Assets/interfaces'
import {
  ErrorBox,
  FormActions,
  FormGrid,
} from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.styles'
import {
  FormType,
  VehicleDetailFormState,
} from '@/lib/features/Assets/components/AddAssetModal/interfaces'
import messages from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.messages'

import { setFormErrors } from '@/lib/utils/forms'

export type VehicleDetailFormProps = {
  onSent?: (response: AddAssetVehicleResponse) => void
}

const schema = z.object({
  model: z.string().nullable().optional(),
  plateNumber: z
    .string({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
  trackingIdentifier: z
    .string({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
  trackingSourceId: z
    .number({ required_error: formsMessages.requiredValidation.defaultMessage })
    .nullable(),
})
export const VehicleDetailForm = ({ onSent }: VehicleDetailFormProps) => {
  const { formatMessage } = useIntl()
  const trackingOptions = useTrackingSourceOptions()

  const { trigger: addAssetVehicle } = useAddVehicle()

  const currentState = useAssetFormState((v) => v.state)
  const lockedForms = useAssetFormState((v) => v.lockedForms)
  const errors = useAssetFormState((v) => v.errors)
  const setAssetFormErrors = useAssetFormState((v) => v.setErrors)
  const patchAssetFormState = useAssetFormState((v) => v.updateState)
  const releaseAllAssetForms = useAssetFormState((v) => v.releaseAllFormTypes)
  const setAssetFormType = useAssetFormState((v) => v.setType)

  const formMethods = useForm<VehicleDetailFormState>({
    defaultValues: {
      model: currentState?.model || null,
      plateNumber: currentState?.plateNumber || null,
      trackingIdentifier: currentState?.trackingIdentifier || null,
      trackingSourceId: currentState?.trackingSourceId || null,
    },
    resolver: zodResolver(schema),
  })

  const { formState, getValues, handleSubmit, setError } = formMethods
  const { isSubmitted, isSubmitting, isValid } = formState

  const onSubmit = async (values: VehicleDetailFormState) => {
    patchAssetFormState(values)
    setAssetFormErrors(null)

    try {
      const response = await addAssetVehicle({
        ...currentState,
        ...values,
      } as AddAssetVehiclePayload)

      if (onSent) {
        onSent(response)
      }
    } catch (err) {
      const apiErrors = err as AddAssetVehicleError

      releaseAllAssetForms()
      setAssetFormErrors(apiErrors)
    }
  }

  useEffect(() => {
    if (errors) {
      const thisFormKeys = Object.keys(getValues())
      const errorEntries = Object.entries(errors).filter(([key]) => key !== '_error')
      const firstStepErrors = errorEntries.some(([fieldKey]) => !thisFormKeys.includes(fieldKey))

      if (firstStepErrors) {
        if (!lockedForms.includes(FormType.VEHICLE_BASIC)) {
          setAssetFormType(FormType.VEHICLE_BASIC)
        }
      } else {
        setFormErrors(setError, errors)
      }
    }
  }, [errors])

  return (
    <FormProvider {...formMethods}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormGrid>
          <TextField
            id="add-asset-form-model"
            type="text"
            name="model"
            displayInline
            disabled={isSubmitting}
            label={formatMessage(messages.model)}
          />
          <TextField
            id="add-asset-form-plateNumber"
            type="text"
            name="plateNumber"
            displayInline
            required
            disabled={isSubmitting}
            label={formatMessage(messages.plateNumber)}
          />
          <SelectField
            aria-labelledby="trackingSourceId"
            name="trackingSourceId"
            label={formatMessage(messages.source)}
            placeholder={formatMessage(messages.select)}
            displayInline
            required
            disabled={isSubmitting}
            selectProps={{
              defaultValue: trackingOptions.find(
                ({ value }) => value === currentState?.trackingSourceId,
              ),
              isDisabled: isSubmitting,
              isSearchable: false,
              maxMenuHeight: 185,
              menuPlacement: 'bottom',
              options: trackingOptions,
            }}
          />
          <TextField
            id="add-asset-form-trackingIdentifier"
            type="text"
            name="trackingIdentifier"
            required
            displayInline
            disabled={isSubmitting}
            label={formatMessage(messages.trackId)}
          />
        </FormGrid>
        {isSubmitted && !isValid && (
          <ErrorBox>
            <FormattedMessage {...messages.customErrorCommunicate} />
          </ErrorBox>
        )}
        <FormActions>
          <Button
            isLoading={isSubmitting}
            minWidth
            $fitToContent
            size="large"
            variant="secondary"
            type="submit"
          >
            <FormattedMessage {...messages.submit} />
          </Button>
        </FormActions>
      </form>
    </FormProvider>
  )
}
